import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Footer from '../containers/footer';
import Header from '../containers/header';
import ContentRow from '../components/book_card/ContentRow.js';
import Loading from '../components/loading/loading.js';
import NewContentModal from '../components/modals/NewContentModal.js';
import { getContentsByCategory } from '../services/contents.js';
import { getCategory } from '../services/categories.js';
import { Box, Chip, IconButton, Pagination, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddIcon from '@mui/icons-material/Add';
import { capitalizeString } from '../helpers/capitalizeString';
import { isAdmin } from '../helpers/helpers_general.js';

import '../components/book_card/book.scss';

const ContentList = () => {
  const { categoryId } = useParams();

  const [states, setStates] = useState({
    page: 1, pages: 1, total: 0, loading: true, contents: [], category: null,
    showNewContentModal: false,
  });
  const [search, setSearch] = useState('');

  const loadPage = (page) => {
    setStates(prev => ({ ...prev, loading: true }));
    getContentsByCategory(categoryId, page).then(response => {
      const data = response.data;
      setStates(prev => ({
        ...prev,
        contents: data.items || data,
        total: data.total || 0,
        pages: data.pages || 1,
        page,
        loading: false,
      }));
    });
  };

  useEffect(() => {
    loadPage(1);
    getCategory(categoryId).then(response =>
      setStates(prev => ({ ...prev, category: response.data }))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const handleSearch = () => {
    if (search.length > 0) window.location.href = '/search/' + search;
  };

  const { category } = states;

  const Cards = () => {
    if (states.contents.length > 0) {
      return (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {states.contents.map((content, index) => (
              <ContentRow
                key={`${content.id}-${index}`}
                contentId={content.id}
                categoryId={categoryId}
                premium={content.premium}
                title={content.title}
                description={content.description}
                link={content.locale}
                document_count={content.document_count}
                extensions={content.extensions}
                updated_at={content.updated_at}
              />
            ))}
          </Box>
          {states.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
              <Pagination
                count={states.pages}
                page={states.page}
                onChange={(_, value) => loadPage(value)}
                color="primary"
                shape="rounded"
              />
            </Box>
          )}
        </>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '50px' }}>
        <Typography variant="h5">
          Não há arquivos para <strong>{capitalizeString(category?.slug || '')}</strong>.
        </Typography>
        <button
          className="card-button btns"
          style={{ width: '204px', marginTop: '24px', padding: '0' }}
          onClick={() => (window.location.href = '/category')}
        >
          Voltar para categorias
        </button>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header loggedIn={true} />

      {/* Hero */}
      <Box sx={{
        position: 'relative',
        width: '100%',
        height: '340px',
        marginTop: '48px',
        flexShrink: 0,
        backgroundColor: '#006494',
        '@media (max-width: 600px)': { height: '240px', marginTop: '56px' },
        backgroundImage: category?.background_url ? `url(${category.background_url})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}>
        {/* overlay com gradiente que some na base */}
        <Box sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.55) 55%, rgba(245,245,245,1) 100%)',
        }} />

        {/* botão voltar + busca no topo */}
        <Box sx={{
          position: 'absolute', top: '48px', left: 0, right: 0, zIndex: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 160px',
          '@media (max-width: 1280px)': { padding: '0 80px' },
          '@media (max-width: 1024px)': { padding: '0 20px' },
          '@media (max-width: 600px)': { padding: '0 12px', top: '16px' },
        }}>
          <IconButton
            onClick={() => window.history.back()}
            sx={{
              color: '#fff', background: 'rgba(0,0,0,0.28)',
              '&:hover': { background: 'rgba(0,0,0,0.5)' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          {/* busca + botão adicionar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '340px', maxWidth: '50vw', position: 'relative', '@media (max-width: 600px)': { maxWidth: '52vw', width: 'auto' } }}>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Buscar arquivos..."
                className="hero-search-input"
                style={{
                  width: '100%',
                  padding: '8px 44px 8px 14px',
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(6px)',
                  WebkitBackdropFilter: 'blur(6px)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
              <IconButton
                onClick={handleSearch}
                sx={{ position: 'absolute', right: 0, color: 'rgba(255,255,255,0.8)' }}
              >
                <SearchOutlinedIcon />
              </IconButton>
            </Box>
            {isAdmin() && (
              <IconButton
                onClick={() => setStates(prev => ({ ...prev, showNewContentModal: true }))}
                sx={{
                  color: '#fff',
                  background: 'rgba(0,100,148,0.7)',
                  backdropFilter: 'blur(6px)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  '&:hover': { background: 'rgba(0,100,148,0.95)' },
                }}
              >
                <AddIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* título + descrição no fundo da foto */}
        <Box sx={{
          position: 'absolute', bottom: '48px', left: 0, right: 0, zIndex: 2,
          padding: '0 160px',
          '@media (max-width: 1280px)': { padding: '0 80px', bottom: '48px' },
          '@media (max-width: 1024px)': { padding: '0 20px', bottom: '40px' },
          '@media (max-width: 600px)': { padding: '0 12px', bottom: '32px' },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '6px' }}>
            <Typography sx={{ color: '#fff', fontSize: '30px', fontWeight: 700, textTransform: 'capitalize', lineHeight: 1.2 }}>
              {category?.slug || ''}
            </Typography>
            {!states.loading && states.total > 0 && (
              <Chip
                label={`${states.total} ${states.total === 1 ? 'conteúdo' : 'conteúdos'}`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.25)',
                  backdropFilter: 'blur(4px)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '12px',
                  border: '1px solid rgba(255,255,255,0.4)',
                }}
              />
            )}
          </Box>
          {category?.description && (
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', maxWidth: '560px' }}>
              {category.description}
            </Typography>
          )}
        </Box>
      </Box>

      {/* lista sobrepondo o hero */}
      <Box sx={{
        padding: '0 160px',
        marginTop: '-40px',
        flexGrow: 1,
        position: 'relative',
        zIndex: 1,
        '@media (max-width: 1280px)': { padding: '0 80px' },
        '@media (max-width: 1024px)': { padding: '0 20px' },
      }}>
        {states.loading ? <Loading /> : <Cards />}
      </Box>

      <NewContentModal
        title="Novo Conteúdo"
        showNewContentModal={states.showNewContentModal}
        setStates={setStates}
        categoryId={categoryId}
      />

      <Footer />
    </Box>
  );
};

export default ContentList;
